import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { BrandsTypes, formatAmount } from "../../../utils/constants";
import Select from "react-select";
import { CSVLink } from "react-csv";
import BrandWiseDetails from "./BrandWiseDetails";

const ConvertedCustomers = () => {
  const [year, setYear] = useState(2025);
  const [selectedBrandType, setSelectedBrandType] = useState(2);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBrandDetails,setShowBrandDetails] = useState(false);
  const [brandDetailsUrl,setBrandDetailsUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: "/review-reports/convertedCustomers",
          method: "POST",
          data: { year, brand_type: selectedBrandType },
        });
        if (result.status === "SUCCESS") {
          setData(result.data?.[0] || null);
        } else {
          Toast("error", result.message ?? "Failed To Fetch Data");
        }
      } catch {
        Toast("error", "An error occurred while fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [year, selectedBrandType]);

  const calculateAvgDaysPerMonth = (activeDays) => {
    if (!activeDays) return 0;
    return (parseFloat(activeDays) / 12).toFixed(2);
  };

  const calculateAvgRevenue = (totalRevenue, activeDays) => {
    if (!totalRevenue || !activeDays) return 0;
    return (parseFloat(totalRevenue) / parseFloat(activeDays)).toFixed(2);
  };

  const generateCSVData = () => {
    if (!data) return [];
    
    const headers = [
      ["Eenadu Accounts Converted Report"],
      [`Year: ${year}`],
      [`Brand Type: ${BrandsTypes?.find(b => +b.value === +selectedBrandType)?.label || 'All'}`],
      [],
      ["Conversion Data"],
      ["Category", "No. of Accounts", `${year} Revenues`, "Av days of activity/ month", "Av revenues/ day of activity", "Insertions"]
    ];
    
    const rows = [
      [`Eenadu ${year} Mapped accounts`, data.eenadu_brand_count || 0, data.eenadu_total_revenue || 0, calculateAvgDaysPerMonth(data.eenadu_active_days), calculateAvgRevenue(data.eenadu_total_revenue, data.eenadu_active_days), data.eenadu_insertions || 0],
      [`W2N ${year} Converted from Mapped accounts`, data.w2n_brand_count || 0, data.w2n_total_revenue || 0, calculateAvgDaysPerMonth(data.w2n_active_days), calculateAvgRevenue(data.w2n_total_revenue, data.w2n_active_days), data.w2n_insertions || 0],
      [`W2N ${year + 1} plan`, "", "", "", "", ""]
    ];
    
    return [...headers, ...rows];
  };

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap gap-6 items-end rounded px-4 py-2 bg-white mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <label className="mr-2 font-semibold text-sm md:text-base">Select Year:</label>
          <DatePicker
            selected={new Date(year, 0, 1)}
            onChange={(date) => setYear(date.getFullYear())}
            showYearPicker
            dateFormat="yyyy"
            className="border border-gray-300 rounded text-center px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
          />
        </div>
        <div className="flex flex-col mb-4 min-w-[180px]">
          <Select
            options={BrandsTypes}
            value={BrandsTypes?.find((b) => +b.value === +selectedBrandType) ?? null}
            onChange={(item) => setSelectedBrandType(item?.value ?? null)}
            placeholder="Select Brand Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">Loading...</div>
        </div>
      ) : data ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-center">Eenadu Accounts converted in {year}: {data.w2n_brand_count}</h2>
            <CSVLink 
              data={generateCSVData()} 
              filename={`converted_customers_${year}.csv`} 
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded text-sm"
            >
              Download CSV
            </CSVLink>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-800 text-sm">
              <thead>
                <tr className="bg-yellow-200">
                  <th className="border border-gray-800 px-4 py-2 text-left"></th>
                  <th className="border border-gray-800 px-4 py-2 text-center">No. of Accounts</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">{year} Revenues</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Av days of activity/ month</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Av revenues/ day of activity</th>
                  <th className="border border-gray-800 px-4 py-2 text-center">Insertions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-yellow-50">
                  <td className="border border-gray-800 px-4 py-2 font-semibold">Eenadu {year} Mapped accounts</td>
                  <td className="border border-gray-800 px-4 py-2 text-center text-blue-500 underline cursor-pointer" title={data.eenadu_brand_count || 0} onClick={() => {setShowBrandDetails(true); setBrandDetailsUrl('/review-reports/convertedBrandWiseDetails')}}>{data.eenadu_brand_count || 0}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={data.eenadu_total_revenue || 0}>{formatAmount(data.eenadu_total_revenue || 0)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgDaysPerMonth(data.eenadu_active_days)}>{calculateAvgDaysPerMonth(data.eenadu_active_days)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgRevenue(data.eenadu_total_revenue, data.eenadu_active_days)}>{formatAmount(calculateAvgRevenue(data.eenadu_total_revenue, data.eenadu_active_days))}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center">{data.eenadu_insertions || 0}</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="border border-gray-800 px-4 py-2 font-semibold">W2N {year} Converted from Mapped accounts</td>
                  <td className="border border-gray-800 px-4 py-2 text-center text-blue-500 underline cursor-pointer" title={data.w2n_brand_count || 0} onClick={() => {setShowBrandDetails(true); setBrandDetailsUrl('/review-reports/convertedCommonBrandWiseDetails')}}>{data.w2n_brand_count || 0}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={data.w2n_total_revenue || 0}>{formatAmount(data.w2n_total_revenue || 0)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgDaysPerMonth(data.w2n_active_days)}>{calculateAvgDaysPerMonth(data.w2n_active_days)}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center" title={calculateAvgRevenue(data.w2n_total_revenue, data.w2n_active_days)}>{formatAmount(calculateAvgRevenue(data.w2n_total_revenue, data.w2n_active_days))}</td>
                  <td className="border border-gray-800 px-4 py-2 text-center">{data.w2n_insertions || 0}</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="border border-gray-800 px-4 py-2 font-semibold">W2N {year + 1} plan</td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                  <td className="border border-gray-800 px-4 py-2 text-center bg-gray-300"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">No data available</div>
        </div>
      )}
      <BrandWiseDetails year={year} newspaper_name='Eenadu' brand_type={selectedBrandType} url={brandDetailsUrl} open={showBrandDetails} onClose={() => setShowBrandDetails(false)} key={showBrandDetails}/>
    </div>
  );
};

export default ConvertedCustomers;
